<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useStore } from '@/core/store'
import { serverService, spectraService } from '@/core/services'
import type Server from '@/core/resources/Server'
import FileBrowser from '@/components/browser/FileBrowser.vue'
import { flashMessage } from '@/utils/FlashMessages'
import { handleError } from '@/utils/ErrorHandler'
import { popupUtil } from '@/utils/Popup'
import router from '@/router'

const store = useStore()
const selectedServer = ref<Server | null>(null)
const servers = ref<Server[]>([])
const browser = ref<InstanceType<typeof FileBrowser> | null>(null)

onMounted(() => {
  serverService.getAll(store.getAccessToken()).then((res) => {
    servers.value = res
  })
})

const selectedSpectraNumber = computed(() => {
  return browser.value?.selectedFilePaths.length.toString() ?? ''
})

const uploadFromFiles = async (fileList: FileList) => {
  const confirmed = await popupUtil.confirm('Are you sure you want to import the selected spectra?')

  if (!confirmed) {
    const fileInput = document.getElementById('file-input')! as HTMLInputElement
    fileInput.files = null
    return
  }

  const formData = new FormData()
  for (const element of fileList) {
    formData.append('files', element)
  }

  spectraService
    .importSpectraFromUpload(store.getAccessToken(), formData)
    .then(() => {
      flashMessage.success('Spectra import successfully launched')
      router.push({ name: 'tasks' })
    })
    .catch(handleError)
}

const uploadFromServer = async () => {
  const selectedFilePaths = browser.value!.selectedFilePaths

  const confirmed = await popupUtil.confirm('Are you sure you want to import the selected spectra?')
  if (!confirmed) return

  spectraService
    .importSpectraFromServer(selectedFilePaths, selectedServer.value!.id, store.getAccessToken())
    .then(() => {
      flashMessage.success('Spectra import successfully launched')
      router.push({ name: 'tasks' })
    })
    .catch(handleError)
}

const refreshBrowser = () => {
  const server = selectedServer.value
  selectedServer.value = null
  setTimeout(() => {
    selectedServer.value = server
  }, 0)
}
</script>

<template>
  <div class="container">
    <h1>Import Spectra</h1>

    <div class="local-upload">
      <span>Upload local spectra:</span>
      <label for="file-input" class="material-symbols-outlined file-input__label" id="file-input-label"> upload_file </label>
      <input type="file" id="file-input" accept=".tgz" multiple @change="uploadFromFiles(($event.target as HTMLInputElement).files!)" />
    </div>

    <div class="server-select" v-if="servers.length > 0">
      <label for="server-select">Or select a server:</label>
      <select v-model="selectedServer" @change="refreshBrowser" id="server-select">
        <option v-for="server in servers" :key="server.id" :value="server">
          {{ server.name }}
        </option>
      </select>

      <button
        id="import-selected"
        @click="uploadFromServer"
        v-if="selectedServer && browser"
        :disabled="browser.selectedFilePaths.length <= 0">
        Import {{ selectedSpectraNumber }} selected spectra
      </button>
    </div>

    <div class="server-select" v-else>
      <p>No server available</p>
    </div>

    <FileBrowser ref="browser" v-if="selectedServer" :server="selectedServer" :import-spectra-mode="true" />

    <div class="footer">
      <button
        id="import-selected"
        @click="uploadFromServer"
        v-if="selectedServer && browser"
        :disabled="browser.selectedFilePaths.length <= 0">
        Import {{ selectedSpectraNumber }} selected spectra
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.container {
  width: clamp(100px, 95%, 1200px);
  margin: 0;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  word-break: keep-all;

  p {
    color: #666;
  }
}

.server-select,
.local-upload {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
}

#file-input {
  display: none;
}

.file-input__label {
  cursor: pointer;
  color: #555;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background-color 0.2s;
  user-select: none;

  &:hover {
    background-color: var(--theme-panel-secondary);
  }
}

select {
  padding: 0.4rem;
  border: 1px solid var(--theme-border-color);
  border-radius: 0.2rem;
  background-color: var(--theme-panel);

  transition: background-color 0.2s;

  &:disabled {
    background-color: var(--theme-panel-secondary);
    color: #666;
  }
}

button,
.button {
  display: flex;
  padding: 0.4rem;
  justify-content: center;
  align-items: center;
  text-transform: uppercase;
  font-size: 0.9rem;

  background-color: #2253a0;
  color: #eee;
  border: none;
  border-radius: 0.2rem;
  cursor: pointer;

  transition: background-color 0.2s;

  span {
    color: inherit;
  }

  &:hover {
    background-color: #1a3f7a;
  }

  &:disabled {
    background-color: var(--theme-panel-secondary);
    cursor: default;
    color: #666;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
}
</style>
